/**
 * Shared category taxonomy for the whole app.
 *
 * Single source of truth used by:
 *   • Recommend page  (manual category picker)
 *   • Gmail Sync      (per-draft category dropdown)
 *   • Add transaction (manual entry)
 *   • Admin rules     (rule.match.categories selector)
 *
 * Keep this list in sync with the canonical categories the engine
 * understands. New categories MUST be added here first, then to the engine.
 *
 * Phase 20 — removed pseudo-categories that were actually TRANSACTION MODES:
 *   'Online' / 'Offline' / 'EMI' / 'International'.
 * These now live in `lib/transactionModes.js` and surface in the UI as a
 * Mode selector orthogonal to Category. Storage shape on the transaction:
 *   { category: 'General' | ..., mode: 'online' | 'offline',
 *     is_emi: bool, is_international: bool }.
 */

export const CATEGORIES = [
  // — Catch-all default (intentionally first so it's the obvious fallback)
  'General',
  // — Eating out & food
  'Dining', 'Quick Service', 'Cafes & Bakeries',
  // — Travel & transport
  'Travel', 'Flights', 'Hotels', 'Ferry', 'Cruise',
  'Fuel', 'FASTag', 'Tolls', 'Public transit',
  'Ride hailing', 'Car rental',
  // — Daily needs
  'Grocery', 'Pharmacy', 'Healthcare',
  // — Payments & utilities
  'UPI', 'Wallets', 'Utilities', 'Rent', 'Insurance',
  'Education', 'Government payments',
  // — Shopping
  'Shopping', 'Amazon', 'Flipkart', 'Electronics',
  'Apparel', 'Gold & jewellery', 'Furniture',
  // — Lifestyle
  'Entertainment', 'Subscriptions', 'Streaming',
  'Fitness', 'Personal care',
  // — Other
  'Other',
];

// Grouped form for nicer UI rendering (Shadcn Select with section headers).
export const CATEGORY_GROUPS = [
  {
    label: 'General',
    items: ['General'],
  },
  {
    label: 'Eating out',
    items: ['Dining', 'Quick Service', 'Cafes & Bakeries'],
  },
  {
    label: 'Travel & transport',
    items: ['Travel', 'Flights', 'Hotels', 'Ferry', 'Cruise',
            'Fuel', 'FASTag', 'Tolls', 'Public transit',
            'Ride hailing', 'Car rental'],
  },
  {
    label: 'Daily needs',
    items: ['Grocery', 'Pharmacy', 'Healthcare'],
  },
  {
    label: 'Payments & utilities',
    items: ['UPI', 'Wallets', 'Utilities', 'Rent', 'Insurance',
            'Education', 'Government payments'],
  },
  {
    label: 'Shopping',
    items: ['Shopping', 'Amazon', 'Flipkart', 'Electronics',
            'Apparel', 'Gold & jewellery', 'Furniture'],
  },
  {
    label: 'Lifestyle',
    items: ['Entertainment', 'Subscriptions', 'Streaming',
            'Fitness', 'Personal care'],
  },
  {
    label: 'Other',
    items: ['Other'],
  },
];

/**
 * Map of soft category aliases → canonical category.
 *
 * Used to normalise free-form category hints emitted by the Gmail GPT-5
 * parser (which sometimes uses "Food" or "Subscription" or "Shopping &
 * Retail") into one of the canonical CATEGORIES above.
 *
 * Phase 20 — legacy values that were previously canonical categories but
 * are now transaction MODES (Online/Offline/EMI/International) get aliased
 * to 'General' here. The rules engine reads them from the `mode`,
 * `is_emi`, `is_international` fields separately.
 */
export const CATEGORY_ALIASES = {
  // food
  'food': 'Dining',
  'restaurants': 'Dining',
  'food & beverage': 'Dining',
  'restaurant': 'Dining',
  'fast food': 'Quick Service',
  'qsr': 'Quick Service',
  'cafe': 'Cafes & Bakeries',
  // travel
  'flight': 'Flights',
  'airline': 'Flights',
  'air travel': 'Flights',
  'hotel': 'Hotels',
  'lodging': 'Hotels',
  'taxi': 'Ride hailing',
  'cab': 'Ride hailing',
  'uber': 'Ride hailing',
  'ola': 'Ride hailing',
  // shopping
  'retail': 'Shopping',
  'shopping & retail': 'Shopping',
  'apparel & accessories': 'Apparel',
  'clothing': 'Apparel',
  'jewellery': 'Gold & jewellery',
  // payments
  'subscription': 'Subscriptions',
  'streaming services': 'Streaming',
  'ott': 'Streaming',
  'mobile recharge': 'Utilities',
  'broadband': 'Utilities',
  'electricity': 'Utilities',
  'fuel & petrol': 'Fuel',
  'petrol': 'Fuel',
  'diesel': 'Fuel',
  // Phase 20 — pseudo-categories that are actually MODES.
  'online': 'General',
  'offline': 'General',
  'emi': 'General',
  'international': 'General',
  'overseas': 'General',
  'forex': 'General',
};

/**
 * Normalise a free-form category string to one of CATEGORIES.
 *
 * Returns 'General' when the input is empty OR when the input is a
 * transaction-mode masquerading as a category. Pass-through everything
 * else (title-cased).
 */
export const normaliseCategory = (hint) => {
  if (!hint) return 'General';
  const cleaned = String(hint).trim().toLowerCase();
  if (!cleaned) return 'General';
  if (CATEGORY_ALIASES[cleaned]) return CATEGORY_ALIASES[cleaned];
  // Try canonical case-insensitive match.
  const exact = CATEGORIES.find((c) => c.toLowerCase() === cleaned);
  if (exact) return exact;
  // Title-case fallback.
  return cleaned
    .split(' ')
    .map((w) => w ? w[0].toUpperCase() + w.slice(1) : '')
    .join(' ');
};
